import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const STAT_COLORS = {
  total: '#6366F1',
  active: '#10B981',
  disabled: '#EF4444',
  creations: '#F59E0B',
};

export default function AdminCreatorsPage() {
  const navigate = useNavigate();
  const { user, isPlatformOwner, isSuperAdmin } = useAuth();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, disabled: 0, totalCreations: 0 });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadCreators = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('creators')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const list = data || [];
      setCreators(list);
      setStats({
        total: list?.length,
        active: list?.filter((c) => !c?.is_disabled)?.length,
        disabled: list?.filter((c) => c?.is_disabled)?.length,
        totalCreations: list?.reduce((sum, c) => sum + (c?.total_creations || 0), 0),
      });
    } catch (err) {
      console.error('[AdminCreators] loadCreators error:', err?.message);
      showToast('Failed to load creators', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPlatformOwner && !isSuperAdmin) {
      navigate('/home-landing-screen');
      return;
    }
    loadCreators();
  }, [isPlatformOwner, isSuperAdmin, loadCreators, navigate]);

  const handleDisableCreator = async (creatorId, currentStatus) => {
    try {
      const { error } = await supabase
        ?.from('creators')
        ?.update({ is_disabled: !currentStatus })
        ?.eq('id', creatorId);
      if (error) throw error;
      showToast(currentStatus ? 'Creator re-enabled' : 'Creator disabled');
      loadCreators();
    } catch (err) {
      showToast('Failed to update creator status', 'error');
    }
  };

  const handleRemoveContent = async (creatorUserId) => {
    if (!window.confirm('Remove all content from this creator? This cannot be undone.')) return;
    try {
      await Promise.all([
        supabase?.from('generated_assets')?.delete()?.eq('user_id', creatorUserId),
        supabase?.from('generated_music')?.delete()?.eq('user_id', creatorUserId),
        supabase?.from('community_gallery')?.delete()?.eq('creator_id', creatorUserId),
      ]);
      showToast('Creator content removed');
      loadCreators();
    } catch (err) {
      showToast('Failed to remove content', 'error');
    }
  };

  const filtered = creators?.filter((c) => {
    if (!searchQuery?.trim()) return true;
    const q = searchQuery?.toLowerCase();
    return c?.display_name?.toLowerCase()?.includes(q) || c?.username?.toLowerCase()?.includes(q);
  });

  if (!isPlatformOwner && !isSuperAdmin) return null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-20 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl"
          style={{
            background: toast?.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${toast?.type === 'success' ? '#10B981' : '#EF4444'}40`,
            backdropFilter: 'blur(12px)',
          }}
        >
          <Icon name={toast?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} color={toast?.type === 'success' ? '#10B981' : '#EF4444'} />
          <span className="text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{toast?.message}</span>
        </div>
      )}
      <main className="main-content">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                  <Icon name="Shield" size={16} color="#EF4444" />
                </div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Admin — Creator Monitor</h1>
              </div>
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Manage creators, review content, and moderate the platform</p>
            </div>
            <button
              onClick={loadCreators}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
              style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name={loading ? 'Loader' : 'RefreshCw'} size={14} color="var(--color-muted-foreground)" />
              Refresh
            </button>
            <button
              onClick={() => navigate('/admin/analytics')}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}
            >
              <Icon name="BarChart2" size={13} color="#F59E0B" />
              Platform Analytics
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Creators', value: stats?.total, icon: 'Users', color: STAT_COLORS?.total },
              { label: 'Active', value: stats?.active, icon: 'CheckCircle2', color: STAT_COLORS?.active },
              { label: 'Disabled', value: stats?.disabled, icon: 'Ban', color: STAT_COLORS?.disabled },
              { label: 'Total Creations', value: stats?.totalCreations, icon: 'Layers', color: STAT_COLORS?.creations },
            ]?.map((stat) => (
              <div key={stat?.label} className="rounded-2xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stat?.color}15` }}>
                    <Icon name={stat?.icon} size={16} color={stat?.color} />
                  </div>
                </div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{stat?.value}</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{stat?.label}</p>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search creators by name or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border outline-none"
              style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
            />
          </div>

          {/* Creators table */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" size={28} color="var(--color-primary)" />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ border: '1px dashed var(--color-border)', borderRadius: 16 }}>
              <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                {searchQuery ? 'No creators match your search' : 'No creators registered yet'}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
              {/* Table header */}
              <div
                className="hidden md:grid grid-cols-[1fr_120px_80px_80px_80px_160px] gap-4 px-4 py-3 text-xs font-semibold"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                <span>Creator</span>
                <span>Username</span>
                <span>Creations</span>
                <span>Followers</span>
                <span>Status</span>
                <span>Actions</span>
              </div>
              {filtered?.map((creator, idx) => (
                <div
                  key={creator?.id}
                  className="flex flex-col md:grid md:grid-cols-[1fr_120px_80px_80px_80px_160px] gap-3 md:gap-4 items-start md:items-center px-4 py-3 transition-all hover:bg-[var(--color-muted)]/30"
                  style={{ borderTop: idx > 0 ? '1px solid var(--color-border)' : 'none', background: 'var(--color-card)' }}
                >
                  {/* Creator info */}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', fontFamily: 'var(--font-heading)' }}
                    >
                      {creator?.display_name?.slice(0, 2)?.toUpperCase() || 'CR'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{creator?.display_name}</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                        Joined {creator?.created_at ? new Date(creator?.created_at)?.toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {/* Username */}
                  <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>@{creator?.username}</span>
                  {/* Creations */}
                  <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{creator?.total_creations || 0}</span>
                  {/* Followers */}
                  <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{creator?.followers_count || 0}</span>
                  {/* Status */}
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{
                      background: creator?.is_disabled ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)',
                      color: creator?.is_disabled ? '#EF4444' : '#10B981',
                      fontFamily: 'var(--font-caption)',
                    }}
                  >
                    {creator?.is_disabled ? 'Disabled' : 'Active'}
                  </span>
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/creator/${creator?.username}`)}
                      className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg transition-all hover:opacity-80"
                      style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
                    >
                      <Icon name="Eye" size={11} color="var(--color-primary)" />
                      View
                    </button>
                    <button
                      onClick={() => handleDisableCreator(creator?.id, creator?.is_disabled)}
                      className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg transition-all hover:opacity-80"
                      style={{
                        background: creator?.is_disabled ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                        color: creator?.is_disabled ? '#10B981' : '#F59E0B',
                        fontFamily: 'var(--font-caption)',
                      }}
                    >
                      <Icon name={creator?.is_disabled ? 'CheckCircle2' : 'Ban'} size={11} color={creator?.is_disabled ? '#10B981' : '#F59E0B'} />
                      {creator?.is_disabled ? 'Enable' : 'Disable'}
                    </button>
                    {creator?.user_id && (
                      <button
                        onClick={() => handleRemoveContent(creator?.user_id)}
                        className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', fontFamily: 'var(--font-caption)' }}
                      >
                        <Icon name="Trash2" size={11} color="#EF4444" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
